import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fileToResizedDataUrl } from "@/lib/image";
import { useMail } from "@/context/MailContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Mail as MailIcon, Inbox, Send as SendIcon, Trash2, Search, RefreshCw, Paperclip,
  Reply, Forward, MailOpen, ChevronLeft, PenSquare, X, Loader2, Folder as FolderIcon,
  Settings, Server, CheckCircle2, EyeOff, Eye,
} from "lucide-react";
import { toast } from "sonner";

const folderIcon = (name) => {
  const n = (name || "").toLowerCase();
  if (n.includes("inbox")) return Inbox;
  if (n.includes("sent") || n.includes("inviat")) return SendIcon;
  if (n.includes("trash") || n.includes("cestino") || n.includes("deleted")) return Trash2;
  return FolderIcon;
};
const folderLabel = (name) => {
  const n = (name || "").toLowerCase();
  if (n === "inbox") return "In arrivo";
  if (n.includes("sent") || n.includes("inviat")) return "Inviata";
  if (n.includes("trash") || n.includes("cestino") || n.includes("deleted")) return "Cestino";
  if (n.includes("draft") || n.includes("bozze")) return "Bozze";
  if (n.includes("junk") || n.includes("spam")) return "Spam";
  return name;
};
const fmtDate = (s) => {
  if (!s) return "";
  const d = new Date(s);
  if (isNaN(d)) return s;
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "2-digit" });
};
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Mail() {
  const { refreshUnread } = useMail();
  const navigate = useNavigate();

  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState("INBOX");
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState(null);

  const [selected, setSelected] = useState(null); // message detail
  const [loadingMsg, setLoadingMsg] = useState(false);
  const [mobileView, setMobileView] = useState("list"); // list | message

  const [composeOpen, setComposeOpen] = useState(false);
  const [compose, setCompose] = useState({ to: "", cc: "", subject: "", body: "", files: [] });
  const [sending, setSending] = useState(false);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [activeFolders, setActiveFolders] = useState([]);
  const [editAcc, setEditAcc] = useState(null); // null = list view; object = form
  const [showPassword, setShowPassword] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [testingSettings, setTestingSettings] = useState(false);

  const pageSize = 20;
  const searchTimer = useRef(null);

  const loadFolders = async () => {
    try {
      const { data } = await api.get("/mail/folders");
      setFolders(data.folders || []);
    } catch { /* ignore, INBOX default */ }
  };

  const loadList = async (opts = {}) => {
    const f = opts.folder ?? folder;
    const p = opts.page ?? page;
    const q = opts.search ?? search;
    setLoadingList(true);
    setListError(null);
    try {
      const { data } = await api.get("/mail/messages", { params: { folder: f, page: p, page_size: pageSize, search: q } });
      setList(data.messages || []);
      setTotal(data.total || 0);
    } catch (e) {
      setListError("Impossibile caricare i messaggi. Controlla la connessione alla casella.");
      setList([]);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => { loadFolders(); refreshUnread(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadList({ page: 1 }); setPage(1); /* eslint-disable-next-line */ }, [folder]);

  const onSearchChange = (v) => {
    setSearch(v);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setPage(1); loadList({ page: 1, search: v }); }, 500);
  };

  const openMessage = async (m) => {
    setLoadingMsg(true);
    setMobileView("message");
    setSelected({ ...m, _loading: true });
    try {
      const { data } = await api.get("/mail/message", { params: { folder, uid: m.uid } });
      setSelected(data);
      // mark seen locally + refresh unread badge
      setList((prev) => prev.map((x) => (x.uid === m.uid ? { ...x, seen: true } : x)));
      refreshUnread();
    } catch {
      toast.error("Errore apertura messaggio");
      setSelected(null);
    } finally {
      setLoadingMsg(false);
    }
  };

  const toggleSeen = async (m, seen) => {
    try {
      await api.post("/mail/mark", { folder, uid: m.uid, seen });
      setList((prev) => prev.map((x) => (x.uid === m.uid ? { ...x, seen } : x)));
      if (selected && selected.uid === m.uid) setSelected({ ...selected, seen });
      refreshUnread();
    } catch { toast.error("Errore aggiornamento stato"); }
  };

  const trashMessage = async (m) => {
    try {
      await api.post("/mail/trash", { folder, uid: m.uid });
      setList((prev) => prev.filter((x) => x.uid !== m.uid));
      if (selected && selected.uid === m.uid) { setSelected(null); setMobileView("list"); }
      toast.success("Spostato nel cestino");
      refreshUnread();
    } catch { toast.error("Errore spostamento nel cestino"); }
  };

  const downloadAttachment = async (att) => {
    try {
      const resp = await api.get("/mail/attachment", { params: { folder, uid: selected.uid, index: att.index }, responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = att.filename || "allegato";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("Errore download allegato"); }
  };

  const openCompose = (preset = {}) => {
    setCompose({ to: "", cc: "", subject: "", body: "", files: [], ...preset });
    setComposeOpen(true);
  };

  const startReply = () => {
    if (!selected) return;
    openCompose({
      to: selected.from_addr,
      subject: selected.subject?.toLowerCase().startsWith("re:") ? selected.subject : `Re: ${selected.subject}`,
      body: `\n\n---\nIl ${selected.date} ${selected.from_name} <${selected.from_addr}> ha scritto:\n`,
      _reply: true,
    });
  };
  const startForward = () => {
    if (!selected) return;
    openCompose({
      subject: selected.subject?.toLowerCase().startsWith("fwd:") ? selected.subject : `Fwd: ${selected.subject}`,
      _forward: true,
    });
  };

  const sendCompose = async () => {
    if (!compose.to.trim()) return toast.error("Inserisci un destinatario");
    setSending(true);
    try {
      if (compose._reply && selected) {
        await api.post("/mail/reply", { folder, uid: selected.uid, body: compose.body, cc: compose.cc });
      } else if (compose._forward && selected) {
        await api.post("/mail/forward", { folder, uid: selected.uid, to: compose.to, body: compose.body });
      } else {
        const fd = new FormData();
        fd.append("to", compose.to);
        fd.append("cc", compose.cc || "");
        fd.append("subject", compose.subject || "");
        fd.append("body", compose.body || "");
        (compose.files || []).forEach((f) => fd.append("files", f));
        await api.post("/mail/send", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Email inviata");
      setComposeOpen(false);
    } catch {
      toast.error("Invio non riuscito. Controlla la connessione alla casella.");
    } finally {
      setSending(false);
    }
  };

  const blankAccount = () => ({
    id: null, label: "", email: "", username: "", password: "",
    imap_host: "", imap_port: 993, imap_ssl: true,
    smtp_host: "", smtp_port: 465, smtp_ssl: true,
    from_name: "HiSocial Italia", signature: "", signature_logo: "", visible_folders: [], has_password: false,
  });

  const loadAccounts = async () => {
    try {
      const { data } = await api.get("/mail/accounts");
      setAccounts(data.accounts || []);
      setActiveFolders(data.active_folders || []);
    } catch {
      toast.error("Impossibile caricare le caselle");
    }
  };

  const openSettings = async () => {
    setSettingsOpen(true);
    setEditAcc(null);
    await loadAccounts();
  };

  const startEdit = (acc) => {
    setShowPassword(false);
    if (acc) {
      setEditAcc({ ...acc, password: "", signature_logo: acc.signature_logo || "", visible_folders: acc.visible_folders || [] });
    } else {
      setEditAcc(blankAccount());
    }
  };

  const toggleShowFolder = (name) => {
    setEditAcc((s) => {
      const list = s.visible_folders || [];
      const visible = list.includes(name) ? list.filter((x) => x !== name) : [...list, name];
      return { ...s, visible_folders: visible };
    });
  };

  const testSettings = async () => {
    setTestingSettings(true);
    try {
      const { data } = await api.post("/mail/accounts/test", editAcc);
      if (data.ok) toast.success("Connessione riuscita");
      else toast.error("Connessione fallita", { description: data.error || "Credenziali o server non validi" });
    } catch {
      toast.error("Errore durante il test");
    } finally {
      setTestingSettings(false);
    }
  };

  const saveSettings = async () => {
    if (!editAcc.email.trim() || !editAcc.imap_host.trim() || !editAcc.smtp_host.trim()) {
      return toast.error("Email, server IMAP e server SMTP sono obbligatori");
    }
    if (!editAcc.id && !editAcc.password.trim()) {
      return toast.error("Inserisci la password per la nuova casella");
    }
    setSavingSettings(true);
    try {
      if (editAcc.id) await api.put(`/mail/accounts/${editAcc.id}`, editAcc);
      else await api.post("/mail/accounts", editAcc);
      toast.success("Casella salvata");
      setEditAcc(null);
      await loadAccounts();
      await loadFolders();
      loadList({ page: 1 });
      refreshUnread();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio non riuscito");
    } finally {
      setSavingSettings(false);
    }
  };

  const activateAccount = async (acc) => {
    try {
      await api.post(`/mail/accounts/${acc.id}/activate`);
      toast.success(`Casella attiva: ${acc.email}`);
      await loadAccounts();
      await loadFolders();
      loadList({ page: 1 });
      refreshUnread();
    } catch {
      toast.error("Impossibile attivare la casella");
    }
  };

  const deleteAccount = async (acc) => {
    if (!window.confirm(`Eliminare la casella ${acc.email}?`)) return;
    try {
      await api.delete(`/mail/accounts/${acc.id}`);
      toast.success("Casella eliminata");
      await loadAccounts();
    } catch {
      toast.error("Impossibile eliminare la casella");
    }
  };

  useEffect(() => {
    loadAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const visibleFolders = useMemo(() => {
    if (!folders.length) return [{ name: "INBOX" }];
    const order = (n) => {
      const l = n.toLowerCase();
      if (l === "inbox") return 0;
      if (l.includes("sent") || l.includes("inviat")) return 1;
      if (l.includes("draft") || l.includes("bozze")) return 2;
      if (l.includes("trash") || l.includes("cestino") || l.includes("deleted")) return 3;
      return 5;
    };
    return [...folders].sort((a, b) => order(a.name) - order(b.name));
  }, [folders]);

  return (
    <div className="tp-fade-in" data-testid="mail-page">
      <div className="flex items-center gap-3 mb-5">
        <div className="h-10 w-10 rounded-xl tp-brand-gradient flex items-center justify-center shrink-0">
          <MailIcon size={18} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">{accounts.find((a) => a.active)?.email || "info@hisocialitalia.it"}</div>
          <h1 className="text-2xl font-semibold tracking-tight text-white">Mail</h1>
        </div>
        {accounts.length > 1 && (
          <select
            data-testid="mail-account-switcher"
            value={accounts.find((a) => a.active)?.id || ""}
            onChange={(e) => {
              const a = accounts.find((x) => x.id === e.target.value);
              if (a && !a.active) activateAccount(a);
            }}
            className="h-9 rounded-full bg-white/5 border border-white/10 text-white text-sm px-3 max-w-[200px] cursor-pointer outline-none"
            title="Cambia casella"
          >
            {accounts.map((a) => (
              <option key={a.id} value={a.id} className="bg-zinc-900">{a.label || a.email}</option>
            ))}
          </select>
        )}
        <button onClick={() => navigate("/amministrazione/task")} className="lg:hidden text-sm text-zinc-400 hover:text-white flex items-center gap-1" data-testid="mail-back-amm">
          <ChevronLeft size={16} /> Amministrazione
        </button>
        <button onClick={openSettings} data-testid="mail-settings-button" title="Impostazioni email"
          className="h-9 w-9 flex items-center justify-center rounded-full text-zinc-400 hover:text-white hover:bg-white/5 transition-colors">
          <Settings size={18} />
        </button>
        <Button onClick={() => openCompose()} data-testid="mail-compose-button" className="tp-brand-btn h-9 rounded-full px-4">
          <PenSquare size={15} className="mr-1.5" /> Scrivi
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[200px_340px_1fr] gap-4 h-[calc(100vh-220px)] min-h-[500px]">
        {/* Folders */}
        <aside className={`tp-glass rounded-2xl p-2 overflow-y-auto ${mobileView === "message" ? "hidden lg:block" : ""}`} data-testid="mail-folders">
          {visibleFolders.map((f) => {
            const Icon = folderIcon(f.name);
            const active = f.name === folder;
            return (
              <button key={f.name} onClick={() => { setFolder(f.name); setSelected(null); setMobileView("list"); }}
                data-testid={`mail-folder-${f.name}`}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm mb-0.5 transition-colors ${active ? "bg-white/10 text-white" : "text-zinc-400 hover:text-white hover:bg-white/5"}`}>
                <Icon size={15} /> <span className="flex-1 text-left truncate">{folderLabel(f.name)}</span>
              </button>
            );
          })}
        </aside>

        {/* Message list */}
        <section className={`tp-glass rounded-2xl flex flex-col overflow-hidden ${mobileView === "message" ? "hidden lg:flex" : "flex"}`}>
          <div className="p-3 border-b border-white/5 flex items-center gap-2">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input data-testid="mail-search" value={search} onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Cerca nella posta..." className="pl-9 h-9 bg-white/5 border-white/10 text-white" />
            </div>
            <button onClick={() => loadList()} title="Aggiorna" data-testid="mail-refresh" className="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5">
              <RefreshCw size={15} className={loadingList ? "animate-spin" : ""} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loadingList ? (
              <div className="p-8 text-center text-sm text-zinc-500 flex items-center justify-center gap-2"><Loader2 size={16} className="animate-spin" /> Caricamento…</div>
            ) : listError ? (
              <div className="p-8 text-center text-sm text-red-300" data-testid="mail-list-error">{listError}<div className="mt-3"><Button variant="outline" onClick={() => loadList()} className="border-white/10 text-zinc-200">Riprova</Button></div></div>
            ) : list.length === 0 ? (
              <div className="p-10 text-center text-sm text-zinc-500 italic">Nessun messaggio.</div>
            ) : (
              list.map((m) => (
                <button key={m.uid} onClick={() => openMessage(m)} data-testid={`mail-row-${m.uid}`}
                  className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/[0.04] transition-colors ${selected && selected.uid === m.uid ? "bg-white/[0.06]" : ""} ${!m.seen ? "bg-fuchsia-500/[0.04]" : ""}`}>
                  <div className="flex items-center gap-2">
                    {!m.seen && <span className="h-2 w-2 rounded-full tp-brand-gradient shrink-0" />}
                    <span className={`flex-1 truncate text-sm ${!m.seen ? "text-white font-semibold" : "text-zinc-300"}`}>{m.from_name}</span>
                    {m.has_attachments && <Paperclip size={12} className="text-zinc-500 shrink-0" />}
                    <span className="text-[11px] text-zinc-500 shrink-0">{fmtDate(m.date)}</span>
                  </div>
                  <div className={`truncate text-sm mt-0.5 ${!m.seen ? "text-zinc-200" : "text-zinc-400"}`}>{m.subject}</div>
                </button>
              ))
            )}
          </div>
          {totalPages > 1 && (
            <div className="p-2 border-t border-white/5 flex items-center justify-between text-xs text-zinc-400">
              <button disabled={page <= 1} onClick={() => { const p = page - 1; setPage(p); loadList({ page: p }); }} className="px-2 py-1 rounded disabled:opacity-30 hover:bg-white/5" data-testid="mail-prev-page">Precedenti</button>
              <span>Pagina {page} di {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => { const p = page + 1; setPage(p); loadList({ page: p }); }} className="px-2 py-1 rounded disabled:opacity-30 hover:bg-white/5" data-testid="mail-next-page">Successivi</button>
            </div>
          )}
        </section>

        {/* Reading pane */}
        <section className={`tp-glass rounded-2xl flex flex-col overflow-hidden ${mobileView === "message" ? "flex" : "hidden lg:flex"}`} data-testid="mail-reading-pane">
          {!selected ? (
            <div className="flex-1 flex items-center justify-center text-sm text-zinc-500 italic p-8">Seleziona un messaggio per leggerlo</div>
          ) : (
            <>
              <div className="p-4 border-b border-white/5">
                <div className="flex items-start gap-2">
                  <button onClick={() => { setMobileView("list"); setSelected(null); }} className="lg:hidden p-1.5 -ml-1.5 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5 shrink-0" data-testid="mail-back-list"><ChevronLeft size={18} /></button>
                  <div className="min-w-0 flex-1">
                    <h2 className="text-lg font-semibold text-white break-words" data-testid="mail-subject">{selected.subject}</h2>
                    <div className="text-sm text-zinc-300 mt-1 truncate"><span className="text-zinc-500">Da:</span> {selected.from_name} &lt;{selected.from_addr}&gt;</div>
                    {selected.to && <div className="text-xs text-zinc-500 truncate"><span>A:</span> {selected.to}</div>}
                    <div className="text-xs text-zinc-500 mt-0.5">{selected.date}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 mt-3 flex-wrap">
                  <Button onClick={startReply} size="sm" variant="outline" className="h-8 border-white/10 text-zinc-200 hover:bg-white/5" data-testid="mail-reply"><Reply size={14} className="mr-1.5" /> Rispondi</Button>
                  <Button onClick={startForward} size="sm" variant="outline" className="h-8 border-white/10 text-zinc-200 hover:bg-white/5" data-testid="mail-forward"><Forward size={14} className="mr-1.5" /> Inoltra</Button>
                  <Button onClick={() => toggleSeen(selected, !selected.seen)} size="sm" variant="outline" className="h-8 border-white/10 text-zinc-200 hover:bg-white/5" data-testid="mail-toggle-seen"><MailOpen size={14} className="mr-1.5" /> Segna {selected.seen ? "non letto" : "letto"}</Button>
                  <Button onClick={() => trashMessage(selected)} size="sm" variant="outline" className="h-8 border-red-400/30 text-red-300 hover:bg-red-500/10" data-testid="mail-trash"><Trash2 size={14} className="mr-1.5" /> Cestina</Button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                {loadingMsg || selected._loading ? (
                  <div className="p-8 text-center text-sm text-zinc-500 flex items-center justify-center gap-2"><Loader2 size={16} className="animate-spin" /> Caricamento…</div>
                ) : (
                  <>
                    {selected.attachments && selected.attachments.length > 0 && (
                      <div className="p-3 border-b border-white/5 flex flex-wrap gap-2">
                        {selected.attachments.map((a) => (
                          <button key={a.index} onClick={() => downloadAttachment(a)} data-testid={`mail-attachment-${a.index}`}
                            className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-zinc-200 hover:bg-white/10">
                            <Paperclip size={12} /> <span className="truncate max-w-[180px]">{a.filename}</span>
                            <span className="text-zinc-500">{Math.max(1, Math.round((a.size || 0) / 1024))} KB</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {selected.body_html ? (
                      <iframe title="email" sandbox="" srcDoc={`<!doctype html><html><head><meta charset="utf-8"><style>body{font-family:-apple-system,Segoe UI,Arial,sans-serif;color:#111;background:#fff;margin:0;padding:16px;font-size:14px;line-height:1.5;word-wrap:break-word}img{max-width:100%}a{color:#be168b}</style></head><body>${selected.body_html}</body></html>`}
                        className="w-full h-full min-h-[300px] bg-white rounded-b-2xl border-0" data-testid="mail-body-html" />
                    ) : (
                      <pre className="p-4 whitespace-pre-wrap break-words text-sm text-zinc-200 font-sans" data-testid="mail-body-text">{selected.body_text}</pre>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </section>
      </div>

      {/* Compose dialog */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="max-w-2xl p-0 tp-glass-strong border-white/10 text-white" data-testid="mail-compose-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-white/10">
            <DialogTitle className="text-white flex items-center gap-2">
              <PenSquare size={18} className="text-fuchsia-400" />
              {compose._reply ? "Rispondi" : compose._forward ? "Inoltra" : "Nuovo messaggio"}
            </DialogTitle>
          </DialogHeader>
          <div className="px-6 py-5 space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-400">A</label>
              <Input data-testid="compose-to" value={compose.to} onChange={(e) => setCompose((c) => ({ ...c, to: e.target.value }))}
                disabled={compose._reply} placeholder="destinatario@esempio.com" className="mt-1 bg-white/5 border-white/10 text-white h-10" />
            </div>
            {!compose._forward && (
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-400">Cc (opzionale)</label>
                <Input data-testid="compose-cc" value={compose.cc} onChange={(e) => setCompose((c) => ({ ...c, cc: e.target.value }))}
                  className="mt-1 bg-white/5 border-white/10 text-white h-10" />
              </div>
            )}
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-400">Oggetto</label>
              <Input data-testid="compose-subject" value={compose.subject} onChange={(e) => setCompose((c) => ({ ...c, subject: e.target.value }))}
                disabled={compose._reply || compose._forward} className="mt-1 bg-white/5 border-white/10 text-white h-10" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-400">Messaggio</label>
              <textarea data-testid="compose-body" value={compose.body} onChange={(e) => setCompose((c) => ({ ...c, body: e.target.value }))}
                rows={10} className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 text-white px-3 py-2 text-sm outline-none focus:border-fuchsia-500 resize-y" />
            </div>
            {!compose._reply && !compose._forward && (
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-400">Allegati</label>
                <input data-testid="compose-files" type="file" multiple onChange={(e) => setCompose((c) => ({ ...c, files: Array.from(e.target.files || []) }))}
                  className="mt-1 w-full text-sm text-zinc-300 file:mr-3 file:rounded-lg file:border-0 file:bg-white/10 file:px-3 file:py-2 file:text-zinc-200 hover:file:bg-white/20" />
                {compose.files.length > 0 && <div className="mt-1 text-xs text-zinc-400">{compose.files.length} file selezionati</div>}
              </div>
            )}
          </div>
          <div className="px-6 py-4 border-t border-white/10 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setComposeOpen(false)} className="border-white/10 text-zinc-300">Annulla</Button>
            <Button onClick={sendCompose} disabled={sending} data-testid="compose-send" className="tp-brand-btn">
              {sending ? <><Loader2 size={15} className="mr-1.5 animate-spin" /> Invio…</> : <><SendIcon size={15} className="mr-1.5" /> Invia</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Settings dialog — account manager */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-xl p-0 tp-glass-strong border-white/10 text-white max-h-[90vh] overflow-hidden flex flex-col" data-testid="mail-settings-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-white/10">
            <DialogTitle className="text-white flex items-center gap-2">
              <Settings size={18} className="text-fuchsia-400" />
              {editAcc ? (editAcc.id ? "Modifica casella" : "Nuova casella") : "Caselle email"}
            </DialogTitle>
          </DialogHeader>

          {!editAcc ? (
            <div className="px-6 py-5 space-y-3 overflow-y-auto">
              {accounts.length === 0 && (
                <div className="text-center text-sm text-zinc-500 py-6">Nessuna casella configurata.</div>
              )}
              {accounts.map((a) => (
                <div key={a.id} data-testid={`mail-account-${a.id}`}
                  className={`rounded-xl border p-3 flex items-center gap-3 ${a.active ? "border-fuchsia-400/40 bg-fuchsia-500/[0.07]" : "border-white/10 bg-white/[0.02]"}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-white truncate">{a.label || a.email}</span>
                      {a.active && <span className="text-[10px] uppercase tracking-wide text-fuchsia-200 bg-fuchsia-500/20 rounded-full px-2 py-0.5">Attiva</span>}
                    </div>
                    <div className="text-[11px] text-zinc-500 truncate">{a.email} · {a.imap_host}</div>
                  </div>
                  {!a.active && (
                    <Button data-testid={`mail-account-activate-${a.id}`} onClick={() => activateAccount(a)}
                      variant="outline" className="h-8 border-white/10 text-zinc-200 hover:bg-white/5 text-xs">Attiva</Button>
                  )}
                  <button data-testid={`mail-account-edit-${a.id}`} onClick={() => startEdit(a)} title="Modifica"
                    className="p-1.5 rounded-lg text-zinc-400 hover:text-white hover:bg-white/10"><Settings size={15} /></button>
                  <button data-testid={`mail-account-delete-${a.id}`} onClick={() => deleteAccount(a)} title="Elimina"
                    className="p-1.5 rounded-lg text-zinc-500 hover:text-red-400 hover:bg-red-500/10"><X size={15} /></button>
                </div>
              ))}
              <Button data-testid="mail-account-add" onClick={() => startEdit(null)} className="tp-brand-btn w-full h-10 rounded-xl mt-2">
                <Server size={15} className="mr-1.5" /> Aggiungi casella
              </Button>
            </div>
          ) : (
            <>
              <div className="px-6 py-5 space-y-4 overflow-y-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-zinc-400">Nome casella</label>
                    <Input data-testid="settings-label" value={editAcc.label}
                      onChange={(e) => setEditAcc((s) => ({ ...s, label: e.target.value }))}
                      placeholder="Es. Info HiSocial" className="mt-1 bg-white/5 border-white/10 text-white h-10" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-zinc-400">Indirizzo email</label>
                    <Input data-testid="settings-email" value={editAcc.email}
                      onChange={(e) => setEditAcc((s) => ({ ...s, email: e.target.value }))}
                      placeholder="info@tuodominio.it" className="mt-1 bg-white/5 border-white/10 text-white h-10" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-zinc-400">Nome utente</label>
                    <Input data-testid="settings-username" value={editAcc.username}
                      onChange={(e) => setEditAcc((s) => ({ ...s, username: e.target.value }))}
                      placeholder="(di solito = email)" className="mt-1 bg-white/5 border-white/10 text-white h-10" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-zinc-400">
                      Password {editAcc.has_password && <span className="text-zinc-500 normal-case">(vuoto = invariata)</span>}
                    </label>
                    <div className="relative mt-1">
                      <Input data-testid="settings-password" type={showPassword ? "text" : "password"} value={editAcc.password}
                        onChange={(e) => setEditAcc((s) => ({ ...s, password: e.target.value }))}
                        placeholder={editAcc.has_password ? "••••••••" : "Password"} className="bg-white/5 border-white/10 text-white h-10 pr-10" />
                      <button type="button" onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white p-1" data-testid="settings-toggle-password">
                        {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                  </div>

                  <div className="sm:col-span-2 flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-400 pt-1">
                    <Server size={13} /> Posta in arrivo (IMAP)
                  </div>
                  <div className="sm:col-span-2">
                    <Input data-testid="settings-imap-host" value={editAcc.imap_host}
                      onChange={(e) => setEditAcc((s) => ({ ...s, imap_host: e.target.value }))}
                      placeholder="imap.tuodominio.it" className="bg-white/5 border-white/10 text-white h-10" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase text-zinc-500">Porta IMAP</label>
                    <Input data-testid="settings-imap-port" type="number" value={editAcc.imap_port}
                      onChange={(e) => setEditAcc((s) => ({ ...s, imap_port: e.target.value }))}
                      className="mt-1 bg-white/5 border-white/10 text-white h-10" />
                  </div>
                  <label className="flex items-center gap-2 mt-5 cursor-pointer" data-testid="settings-imap-ssl">
                    <input type="checkbox" checked={!!editAcc.imap_ssl}
                      onChange={(e) => setEditAcc((s) => ({ ...s, imap_ssl: e.target.checked }))}
                      className="accent-fuchsia-500 h-4 w-4" />
                    <span className="text-sm text-zinc-300">SSL/TLS</span>
                  </label>

                  <div className="sm:col-span-2 flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-400 pt-1">
                    <SendIcon size={13} /> Posta in uscita (SMTP)
                  </div>
                  <div className="sm:col-span-2">
                    <Input data-testid="settings-smtp-host" value={editAcc.smtp_host}
                      onChange={(e) => setEditAcc((s) => ({ ...s, smtp_host: e.target.value }))}
                      placeholder="smtp.tuodominio.it" className="bg-white/5 border-white/10 text-white h-10" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase text-zinc-500">Porta SMTP</label>
                    <Input data-testid="settings-smtp-port" type="number" value={editAcc.smtp_port}
                      onChange={(e) => setEditAcc((s) => ({ ...s, smtp_port: e.target.value }))}
                      className="mt-1 bg-white/5 border-white/10 text-white h-10" />
                  </div>
                  <label className="flex items-center gap-2 mt-5 cursor-pointer" data-testid="settings-smtp-ssl">
                    <input type="checkbox" checked={!!editAcc.smtp_ssl}
                      onChange={(e) => setEditAcc((s) => ({ ...s, smtp_ssl: e.target.checked }))}
                      className="accent-fuchsia-500 h-4 w-4" />
                    <span className="text-sm text-zinc-300">SSL/TLS</span>
                  </label>

                  <div className="sm:col-span-2">
                    <label className="text-xs uppercase tracking-wider text-zinc-400">Nome mittente</label>
                    <Input data-testid="settings-from-name" value={editAcc.from_name}
                      onChange={(e) => setEditAcc((s) => ({ ...s, from_name: e.target.value }))}
                      className="mt-1 bg-white/5 border-white/10 text-white h-10" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-xs uppercase tracking-wider text-zinc-400">Firma</label>
                    <textarea data-testid="settings-signature" value={editAcc.signature}
                      onChange={(e) => setEditAcc((s) => ({ ...s, signature: e.target.value }))}
                      rows={3} placeholder="Firma email (opzionale)"
                      className="mt-1 w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white outline-none focus:border-fuchsia-500/40" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-xs uppercase tracking-wider text-zinc-400">Logo firma</label>
                    <div className="mt-1 flex items-center gap-3">
                      {editAcc.signature_logo ? (
                        <img src={editAcc.signature_logo} alt="logo" data-testid="settings-signature-logo-preview"
                          className="h-14 w-auto max-w-[160px] rounded-md border border-white/10 bg-white/5 object-contain p-1" />
                      ) : (
                        <div className="h-14 w-14 rounded-md border border-dashed border-white/15 flex items-center justify-center text-zinc-600 text-[10px]">Nessun logo</div>
                      )}
                      <label className="cursor-pointer inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-xs text-zinc-200 hover:bg-white/5">
                        <Paperclip size={13} /> Carica
                        <input type="file" accept="image/*" className="hidden" data-testid="settings-signature-logo-input"
                          onChange={async (e) => {
                            const f = e.target.files?.[0];
                            if (!f) return;
                            try {
                              const dataUrl = await fileToResizedDataUrl(f, 400, 0.85);
                              setEditAcc((s) => ({ ...s, signature_logo: dataUrl }));
                            } catch { toast.error("Immagine non valida"); }
                            e.target.value = "";
                          }} />
                      </label>
                      {editAcc.signature_logo && (
                        <button type="button" data-testid="settings-signature-logo-remove"
                          onClick={() => setEditAcc((s) => ({ ...s, signature_logo: "" }))}
                          className="p-2 rounded-lg text-zinc-500 hover:text-red-400 hover:bg-red-500/10"><X size={14} /></button>
                      )}
                    </div>
                  </div>
                </div>

                {editAcc.active && activeFolders.length > 0 && (
                  <div className="pt-2">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-400 mb-2">
                      <Eye size={13} /> Cartelle da mostrare
                    </div>
                    <div className="text-[11px] text-zinc-500 mb-2">Spunta solo le cartelle da visualizzare. Quelle non spuntate vengono nascoste.</div>
                    <div className="rounded-xl border border-white/10 divide-y divide-white/5 max-h-40 overflow-y-auto">
                      {activeFolders.map((name) => {
                        const visible = (editAcc.visible_folders || []).includes(name) || (editAcc.visible_folders || []).length === 0;
                        return (
                          <label key={name} data-testid={`settings-folder-${name}`}
                            className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-white/[0.03]">
                            <input type="checkbox" checked={visible} onChange={() => toggleShowFolder(name)}
                              className="accent-fuchsia-500 h-4 w-4" />
                            <span className={`text-sm truncate ${visible ? "text-zinc-200" : "text-zinc-500 line-through"}`}>{name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              <div className="px-6 py-4 border-t border-white/10 flex items-center justify-between gap-2">
                <Button variant="outline" onClick={testSettings} disabled={testingSettings} data-testid="settings-test"
                  className="border-white/10 text-zinc-200 hover:bg-white/5">
                  {testingSettings ? <><Loader2 size={15} className="mr-1.5 animate-spin" /> Test…</> : <><CheckCircle2 size={15} className="mr-1.5" /> Testa</>}
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setEditAcc(null)} className="border-white/10 text-zinc-300">Indietro</Button>
                  <Button onClick={saveSettings} disabled={savingSettings} data-testid="settings-save" className="tp-brand-btn">
                    {savingSettings ? <><Loader2 size={15} className="mr-1.5 animate-spin" /> Salvataggio…</> : "Salva"}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
